import React from 'react';
import { useSelector } from 'react-redux';
import { FlagProvider, IConfig, IToggle, UnleashClient } from '@unleash/proxy-client-react';

import { track } from 'apis/amethyst';
import { evABTestEnrollment } from 'events/ab';

export type UnleashBootstrapToggles = IToggle[];
export type UnleashConfig = IConfig;

type Props = {
  children: React.ReactNode;
  config: UnleashConfig;
};

type UnleashEventContext = {
  sessionId: number;
  appName: string;
  environment: string;
};

interface UnleashImpressionEvent {
  eventId: string;
  context: UnleashEventContext;
  enabled: boolean;
  featureName: string;
}

// kill switch style toggle.
interface UnleashIsEnabledEvent extends UnleashImpressionEvent {
  eventType: 'isEnabled';
}

// ab test.
interface GetVariantEvent extends UnleashImpressionEvent {
  eventType: 'getVariant';
  variant: string;
}

function isVariantEvent(event: UnleashIsEnabledEvent | GetVariantEvent): event is GetVariantEvent {
  return event.eventType === 'getVariant';
}

let unleash: UnleashClient;
function startUnleashClient(config: UnleashConfig, bootstrap: UnleashBootstrapToggles) {
  // if its already init just return it;
  if (unleash) {
    return unleash;
  }

  unleash = new UnleashClient({ ...config, bootstrap });

  unleash.on('impression', (event: UnleashIsEnabledEvent | GetVariantEvent) => {
    // only track enabled ab test "variant" events.
    if (event.enabled && isVariantEvent(event)) {
      const abTest = {
        test: event.featureName,
        phase: 0,
        group: Number.parseInt(event.variant),
        variation: event.variant
      };

      track(() => [evABTestEnrollment, abTest]);
    }
  });

  unleash.start();
  return unleash;
}

const UnleashFlagProvider = ({ children, config }: Props) => {
  const bootstrap = useSelector(({ unleash }) => unleash.toggles);
  // configureEnvironment.js pulls config from marketplace.json if exists
  const unleash = startUnleashClient(config, bootstrap);

  return (
    <FlagProvider unleashClient={unleash} startClient={false}>
      {children}
    </FlagProvider>
  );
};

export default UnleashFlagProvider;
